import React, { useEffect, useMemo, useRef, useState } from 'react';
import { portalService } from '../../../services';
import { useAdminShell } from './AdminShellContext';
import useAreaHook, { RADIUS_OPTIONS, milesBetween } from './useArea';
import { MAGENTA, ringBorder } from './ui';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

/** "British Columbia, Canada" → "BC"; "WA, USA" → "WA". The tag has room for a code. */
function placeCode(region, label) {
  const first = String(region || label || '').split(',')[0].trim();
  if (!first) return '';
  const words = first.split(/\s+/).filter(Boolean);
  if (words.length === 1) return words[0].length <= 3 ? words[0].toUpperCase() : words[0].slice(0, 3).toUpperCase();
  return words.map((w) => w[0]).join('').toUpperCase().slice(0, 3);
}

const PinGlyph = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" className="flex-shrink-0" aria-hidden="true">
    <path d="M12 22s-7.5-6.6-7.5-12.2A7.5 7.5 0 0 1 19.5 9.8C19.5 15.4 12 22 12 22z" fill={MAGENTA} />
    <circle cx="12" cy="9.8" r="2.8" fill="#fff" />
  </svg>
);

/**
 * The AREA scope bar — one city, one radius, then PULL. Shared by EVENTS, PICK EVENTS,
 * FEATURED, PARTY PATRONS, PARTY ENGINE and LISTED PROS: the city is typed into the
 * draft and nothing is re-queried until PULL (the status dot is amber until then), so
 * a section never fires six requests while someone types.
 *
 * `note` is the section's sentence, given the resolved state. `stats` is the row of
 * value/label pills on the right of the status line. `pullLabel` names the pull.
 */
export default function AreaBar({ note, stats, pullLabel = 'PULL EVENTS →', onPull }) {
  const shell = useAdminShell();
  // The shell owns the area; a page rendered outside it (a test, a preview) gets its own.
  const own = useAreaHook();
  const area = shell.area || own;
  const [cities, setCities] = useState([]);
  const [found, setFound] = useState([]);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const searchTimer = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await portalService.listCities();
      if (!cancelled && r.success) setCities(r.data || []);
    })();
    return () => { cancelled = true; };
  }, []);

  // The curated catalogue is short, so anything else is geocoded — scoping to a city we
  // have never held an event in is the whole point of the AREA bar.
  useEffect(() => {
    clearTimeout(searchTimer.current);
    const term = q.trim();
    if (term.length < 2 || !MAPBOX_TOKEN) { setFound([]); return undefined; }
    searchTimer.current = setTimeout(async () => {
      try {
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(term)}.json`
          + `?types=place,locality&limit=6&language=en&access_token=${MAPBOX_TOKEN}`;
        const data = await (await fetch(url)).json();
        setFound((data.features || []).map((f) => {
          const ctx = f.context || [];
          const region = ctx.find((c) => (c.id || '').startsWith('region.'))?.text;
          const country = ctx.find((c) => (c.id || '').startsWith('country.'))?.text;
          const [lng, lat] = f.center || [];
          return { label: [f.text, region, country].filter(Boolean).join(', '), region: [region, country].filter(Boolean).join(', '), lat, lng };
        }).filter((r) => Number.isFinite(r.lat)));
      } catch { setFound([]); }
    }, 250);
    return () => clearTimeout(searchTimer.current);
  }, [q]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const suggestions = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const from = area.area;
    const catalogue = (cities || [])
      .filter((c) => !needle || (c.label || c.name || '').toLowerCase().includes(needle))
      .map((c) => ({ label: c.label || c.name, lat: c.lat, lng: c.lng, region: c.region || '' }));
    const seen = new Set(catalogue.map((c) => c.label.toLowerCase()));
    const rest = found.filter((f) => !seen.has(f.label.toLowerCase()));
    return [...catalogue, ...rest]
      .map((c) => {
        const miles = from ? milesBetween(from.lat, from.lng, c.lat, c.lng) : null;
        return { ...c, miles, meta: miles == null ? c.region : `${Math.round(miles)} MI AWAY` };
      })
      .sort((a, b) => (a.miles == null || b.miles == null ? 0 : a.miles - b.miles))
      .slice(0, 12);
  }, [cities, found, q, area.area]);

  const text = typeof note === 'function' ? note(area) : note;
  const place = area.draft ? placeCode(area.draft.region, area.draft.label.split(',').slice(1).join(',')) : '';
  const cityTag = area.draft ? `${place ? `${place} · ` : ''}${area.draftRadius} MI` : null;

  const doPull = () => {
    const next = area.pull();
    setOpen(false);
    onPull?.(next, area.draftRadius);
  };

  return (
    <div className="relative z-20 flex flex-col gap-3 rounded-[24px] bg-white p-4">
      <div className="flex flex-wrap items-center gap-[10px]">
        <div ref={wrapRef} className={`relative min-w-0 flex-1 basis-[240px] ${shell.isMobile ? '' : 'max-w-[380px]'}`}>
          <div className="flex h-12 items-center gap-[10px] rounded-full bg-[#F2F2F2] pl-4 pr-2">
            <PinGlyph />
            <input
              value={q}
              onChange={(e) => { setQ(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              placeholder={area.draft ? area.draft.label.toUpperCase() : 'EVERYWHERE'}
              aria-label="Scope to a city"
              className="min-w-0 flex-1 border-0 bg-transparent p-0 font-mono text-[13px] font-extrabold uppercase tracking-[0.12em] text-black placeholder:text-[#9CA3AF] focus:outline-none focus:ring-0"
            />
            {cityTag && <span className="flex h-8 flex-shrink-0 items-center whitespace-nowrap rounded-full bg-white px-3 font-mono text-[10px] font-bold uppercase tracking-[0.1em] text-[#6B7280]">{cityTag}</span>}
          </div>
          {open && suggestions.length > 0 && (
            <div className="absolute left-0 right-0 top-[calc(100%+8px)] z-50 max-h-[300px] overflow-y-auto rounded-[24px] bg-[#0A0A0A] p-2">
              {suggestions.map((s) => (
                <button
                  key={`${s.label}-${s.lat}`}
                  type="button"
                  onMouseDown={() => { area.setDraft({ label: s.label, lat: s.lat, lng: s.lng, region: s.region }); setQ(''); setOpen(false); }}
                  className="flex min-h-[44px] w-full items-center justify-between gap-[10px] rounded-[16px] px-[14px] text-left font-mono text-[12px] font-bold uppercase tracking-[0.08em] text-white hover:bg-[#1F1F1F]"
                >
                  <span className="flex items-center gap-[10px]"><PinGlyph size={14} />{s.label}</span>
                  <span className="font-mono text-[10px] font-normal text-[#8A8A8A]">{s.meta}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className={`pr-scroll-x flex max-w-full gap-[2px] rounded-full bg-[#F2F2F2] p-[3px] ${shell.isMobile ? 'w-full' : 'flex-shrink-0'}`} role="radiogroup" aria-label="Search radius">
          {RADIUS_OPTIONS.map((m) => {
            const on = area.draftRadius === m;
            return (
              <button
                key={m}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => area.setDraftRadius(m)}
                className={`h-10 whitespace-nowrap rounded-full px-[13px] font-mono text-[11px] font-bold tracking-[0.06em] transition-colors duration-200 ${on ? 'bg-black text-white' : 'text-black hover:bg-white'}`}
              >
                {m} MI
              </button>
            );
          })}
        </div>
        {/* On a phone the two buttons share one row, the radius track scrolls above them. */}
        <div className={`flex gap-[10px] ${shell.isMobile ? 'w-full' : 'flex-shrink-0'}`}>
          <button type="button" onClick={doPull} className={`h-12 flex-shrink-0 rounded-full px-[22px] font-mono text-[11px] font-extrabold uppercase tracking-[0.14em] text-white hover:opacity-85 ${shell.isMobile ? 'flex-1' : ''}`} style={ringBorder('#000')}>{pullLabel}</button>
          <button type="button" onClick={() => { area.setDraft(null); setQ(''); area.pull(); onPull?.(null, area.draftRadius); }} className={`h-12 flex-shrink-0 rounded-full bg-[#F2F2F2] px-4 font-mono text-[10px] font-bold uppercase tracking-[0.12em] hover:bg-[#E5E5E5] ${shell.isMobile ? 'flex-1' : ''}`}>EVERYWHERE</button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 px-1 pt-1">
        <span className="flex flex-1 basis-[260px] items-center gap-2 font-mono text-[11px] leading-[1.5] text-[#4B5563]">
          <span className="h-2 w-2 flex-shrink-0 rounded-full" style={{ background: area.dirty ? '#FACC15' : '#16A34A' }} />
          {text || (area.dirty ? 'Not pulled yet.' : area.area ? `Everything within ${area.radiusMiles} mi of ${area.area.label}.` : 'Everywhere.')}
        </span>
        {(stats || []).map((s) => (
          <span key={s.label} className="flex h-[34px] items-center gap-[6px] whitespace-nowrap rounded-full bg-[#F7F7F7] px-[14px] font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-[#6B7280]">
            <span className="font-mono text-[14px] font-extrabold tracking-normal text-black">{s.value}</span>{s.label}
          </span>
        ))}
      </div>
    </div>
  );
}
